import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DashboardShell from "@/components/layout/DashboardShell";
import { useT } from "@/lib/i18n";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Power, Mail, MailCheck, Pencil, ArrowUp, ArrowDown, Archive as ArchiveIcon, RotateCcw, Trash2, AlertTriangle, Store } from "lucide-react";

const PLAN_LABEL = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

const isoDateInput = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
};

function IconBtn({ label, testId, onClick, disabled, children, variant = "outline", className = "" }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            data-testid={testId}
            onClick={onClick}
            disabled={disabled}
            variant={variant}
            size="icon"
            aria-label={label}
            className={`rounded-full h-9 w-9 shrink-0 ${className}`}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AdminSubscribers() {
  const { t } = useT();
  const [tab, setTab] = useState("active"); // "active" | "archived"
  const [subs, setSubs] = useState([]);
  const [archived, setArchived] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ owner_name: "", owner_mobile: "", owner_email: "", haat_name: "", plan_id: "starter", village: "", district: "", state: "" });
  const [saving, setSaving] = useState(false);

  // Edit dialog
  const [editingSub, setEditingSub] = useState(null); // full row when set
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  // Confirm dialog (upgrade / downgrade / archive / restore / permanent delete)
  const [confirm, setConfirm] = useState(null); // {kind, sub, endpoint, verb, message, danger?}
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Multi-haat management (Enterprise)
  const [haatsFor, setHaatsFor] = useState(null); // subscriber row
  const [haatsData, setHaatsData] = useState(null); // {haats, haat_limit}
  const [newHaat, setNewHaat] = useState("");
  const [haatBusy, setHaatBusy] = useState(false);

  const openHaats = async (s) => {
    setHaatsFor(s); setHaatsData(null); setNewHaat("");
    try { const r = await api.get(`/admin/subscribers/${s.subscriber_id}/haats`); setHaatsData(r.data); }
    catch { toast.error("Failed to load haats"); }
  };

  const addHaatFor = async () => {
    if (newHaat.trim().length < 2) { toast.error("Enter a haat name"); return; }
    setHaatBusy(true);
    try {
      await api.post(`/admin/subscribers/${haatsFor.subscriber_id}/haats`, { name: newHaat });
      toast.success("Pashu Haat added");
      setNewHaat("");
      const r = await api.get(`/admin/subscribers/${haatsFor.subscriber_id}/haats`);
      setHaatsData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setHaatBusy(false); }
  };

  const loadActive = () => api.get("/admin/subscribers").then((r) => setSubs(r.data));
  const loadArchived = () => api.get("/admin/subscribers?archived=true").then((r) => setArchived(r.data));
  useEffect(() => { loadActive(); loadArchived(); }, []);

  const save = async () => {
    if (!form.owner_name || !form.owner_mobile || !form.haat_name) { toast.error("Name, mobile and haat name required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/subscribers", form);
      toast.success("Subscriber added");
      setOpen(false);
      setForm({ owner_name: "", owner_mobile: "", owner_email: "", haat_name: "", plan_id: "starter", village: "", district: "", state: "" });
      loadActive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/admin/subscribers/${id}/toggle`); loadActive(); } catch { toast.error("Failed"); }
  };

  const resendInvite = async (s) => {
    if (!s.owner_email) { toast.error("Add an email to this subscriber first"); return; }
    try {
      const r = await api.post(`/admin/subscribers/${s.subscriber_id}/resend-invite`);
      toast.success(`Google invite queued to ${r.data.queued_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
  };

  const openEdit = (s) => {
    setEditingSub(s);
    setEditForm({
      owner_name: s.owner_name || "",
      owner_mobile: s.owner_mobile || "",
      haat_name: s.haat_name || "",
      village: s.village || "",
      district: s.district || "",
      state: s.state || "",
      plan_id: s.plan_id || "starter",
      subscription_expires_at: isoDateInput(s.subscription_expires_at),
    });
  };

  const submitEdit = async () => {
    if (!editingSub) return;
    if (!editForm.owner_name || !editForm.owner_mobile || !editForm.haat_name) {
      toast.error("Name, mobile and haat name are required"); return;
    }
    setEditBusy(true);
    try {
      const payload = { ...editForm };
      // Convert the local YYYY-MM-DD back to ISO (end-of-day UTC) so downgrade schedule reads it consistently.
      payload.subscription_expires_at = editForm.subscription_expires_at
        ? new Date(`${editForm.subscription_expires_at}T23:59:59Z`).toISOString()
        : null;
      await api.patch(`/admin/subscribers/${editingSub.subscriber_id}`, payload);
      toast.success("Subscriber updated");
      setEditingSub(null);
      loadActive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setEditBusy(false); }
  };

  const askConfirm = (sub, kind) => {
    const map = {
      upgrade:   { endpoint: `/admin/subscribers/${sub.subscriber_id}/upgrade`,   verb: "Upgrade",  message: `Upgrade ${sub.haat_name} to the next plan? Billing is prorated immediately.` },
      downgrade: { endpoint: `/admin/subscribers/${sub.subscriber_id}/downgrade`, verb: "Downgrade", message: `Schedule downgrade for ${sub.haat_name}. Change applies from the next billing cycle.` },
      archive:   { endpoint: `/admin/subscribers/${sub.subscriber_id}/archive`,   verb: "Archive",   message: `Archive ${sub.haat_name}? It will be moved out of Active Subscribers. You can restore later.` },
      restore:   { endpoint: `/admin/subscribers/${sub.subscriber_id}/restore`,   verb: "Restore",   message: `Restore ${sub.haat_name} to Active Subscribers?` },
      permanent: { endpoint: `/admin/subscribers/${sub.subscriber_id}`,           verb: "Permanently delete", method: "delete", danger: true,
                   message: `Permanently delete ${sub.haat_name}? This removes the subscriber and its owner login FOREVER and cannot be undone.` },
    };
    setConfirm({ kind, sub, ...map[kind] });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      const method = confirm.method || "post";
      await api[method](confirm.endpoint);
      toast.success(`${confirm.verb} done`);
      setConfirm(null);
      loadActive();
      loadArchived();
    } catch (e) { toast.error(e?.response?.data?.detail || `${confirm.verb} failed`); }
    finally { setConfirmBusy(false); }
  };

  const canUpgrade = (p) => p === "starter" || p === "professional";
  const canDowngrade = (p) => p === "professional" || p === "enterprise";

  return (
    <DashboardShell
      title={t("nav.subscribers")}
      subtitle={t("admin.subscribersSubtitle")}
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TEST.adminAddSubscriber} className="am-btn-primary"><Plus className="w-4 h-4 mr-1.5" /> {t("admin.addSubscriber")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{t("admin.addSubscriber")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder={`${t("admin.owner")} ${t("common.name").toLowerCase()}`} value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="am-input" />
              <Input placeholder={t("common.mobile")} inputMode="numeric" value={form.owner_mobile} onChange={(e) => setForm({ ...form, owner_mobile: e.target.value.replace(/\D/g, "") })} className="am-input" />
              <Input data-testid="admin-add-subscriber-email" placeholder="Email (for Google sign-in, optional)" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="am-input" />
              <Input placeholder={t("admin.haat")} value={form.haat_name} onChange={(e) => setForm({ ...form, haat_name: e.target.value })} className="am-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Village" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} className="am-input" />
                <Input placeholder="District" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="am-input" />
              </div>
              <Input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="am-input" />
              <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                <SelectTrigger className="am-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter — ₹1,999 · 5 helpers</SelectItem>
                  <SelectItem value="professional">Professional — ₹2,999 · 10 helpers</SelectItem>
                  <SelectItem value="enterprise">Enterprise — ₹4,999 · Unlimited helpers · 2 haats</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button data-testid={TEST.adminSaveSubscriber} disabled={saving} onClick={save} className="am-btn-primary w-full">{saving ? "…" : t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-4" data-testid="admin-subs-tabs">
          <TabsTrigger value="active" data-testid="admin-subs-tab-active">Active ({subs.length})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="admin-subs-tab-archived">Archived ({archived.length})</TabsTrigger>
        </TabsList>

        {/* ACTIVE TAB */}
        <TabsContent value="active" className="mt-0">
          <div className="am-card p-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                  <th className="px-4 py-3">{t("admin.haat")}</th>
                  <th className="px-4 py-3">{t("admin.owner")}</th>
                  <th className="px-4 py-3">{t("admin.plan")}</th>
                  <th className="px-4 py-3">{t("admin.helpers")}</th>
                  <th className="px-4 py-3">{t("common.status")}</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.subscriber_id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{s.haat_name}</div>
                      <div className="text-xs text-muted-foreground">{[s.village, s.district, s.state].filter(Boolean).join(", ") || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{s.owner_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.owner_mobile}</div>
                      {s.owner_email ? (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-primary" title={s.owner_email}>
                          <MailCheck className="w-3 h-3" /> Google-ready
                        </div>
                      ) : (
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">No email</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="capitalize font-medium">{PLAN_LABEL[s.plan_id] || s.plan_id}</div>
                      {s.pending_plan_id && (
                        <div className="text-[10px] font-bold uppercase text-amber-600 mt-0.5" title={s.pending_plan_effective_at}>
                          ↓ {PLAN_LABEL[s.pending_plan_id]} pending
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono">{s.helper_limit}</td>
                    <td className="px-4 py-3">
                      <span className={`am-chip ${s.is_active ? "" : "bg-muted text-muted-foreground"}`}>
                        {s.is_active ? t("common.active") : t("common.disabled")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {s.owner_email && (
                          <IconBtn label={`Send Google invite to ${s.owner_email}`} testId={`admin-resend-invite-${s.subscriber_id}`} onClick={() => resendInvite(s)}>
                            <Mail className="w-4 h-4" />
                          </IconBtn>
                        )}
                        <IconBtn label={s.is_active ? t("admin.disable") : t("admin.enable")} testId={TEST.adminToggleSub(s.subscriber_id)} onClick={() => toggle(s.subscriber_id)}>
                          <Power className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label="Edit subscriber" testId={`admin-edit-${s.subscriber_id}`} onClick={() => openEdit(s)}>
                          <Pencil className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label="Manage Pashu Haats" testId={`admin-haats-${s.subscriber_id}`} onClick={() => openHaats(s)}>
                          <Store className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label={canUpgrade(s.plan_id) ? "Upgrade plan" : "Already on top plan"} testId={`admin-upgrade-${s.subscriber_id}`} disabled={!canUpgrade(s.plan_id)} onClick={() => askConfirm(s, "upgrade")}>
                          <ArrowUp className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label={canDowngrade(s.plan_id) ? "Downgrade plan" : "Already on lowest plan"} testId={`admin-downgrade-${s.subscriber_id}`} disabled={!canDowngrade(s.plan_id)} onClick={() => askConfirm(s, "downgrade")}>
                          <ArrowDown className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label="Archive subscriber" testId={`admin-archive-${s.subscriber_id}`} onClick={() => askConfirm(s, "archive")} className="text-destructive hover:text-destructive">
                          <ArchiveIcon className="w-4 h-4" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
                {subs.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">{t("sub.noData")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ARCHIVED TAB */}
        <TabsContent value="archived" className="mt-0">
          <div className="am-card p-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                  <th className="px-4 py-3">{t("admin.haat")}</th>
                  <th className="px-4 py-3">{t("admin.owner")}</th>
                  <th className="px-4 py-3">{t("admin.plan")}</th>
                  <th className="px-4 py-3">Archived on</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {archived.map((s) => (
                  <tr key={s.subscriber_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`admin-archived-row-${s.subscriber_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{s.haat_name}</div>
                      <div className="text-xs text-muted-foreground">{[s.village, s.district, s.state].filter(Boolean).join(", ") || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{s.owner_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{s.owner_mobile}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{PLAN_LABEL[s.plan_id] || s.plan_id}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                      {s.archived_at ? new Date(s.archived_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <IconBtn label="Restore to Active" testId={`admin-restore-${s.subscriber_id}`} onClick={() => askConfirm(s, "restore")}>
                          <RotateCcw className="w-4 h-4" />
                        </IconBtn>
                        <IconBtn label="Permanent delete (Super Admin only)" testId={`admin-perma-delete-${s.subscriber_id}`} onClick={() => askConfirm(s, "permanent")} className="text-destructive hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
                {archived.length === 0 && (
                  <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">No archived subscribers.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* EDIT DIALOG */}
      <Dialog open={!!editingSub} onOpenChange={(v) => { if (!v) setEditingSub(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Edit subscriber</DialogTitle></DialogHeader>
          {editingSub && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Haat name</label>
                <Input data-testid="admin-edit-haat-name" value={editForm.haat_name} onChange={(e) => setEditForm({ ...editForm, haat_name: e.target.value })} className="am-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Owner name</label>
                  <Input data-testid="admin-edit-owner-name" value={editForm.owner_name} onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })} className="am-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Mobile</label>
                  <Input data-testid="admin-edit-owner-mobile" inputMode="numeric" value={editForm.owner_mobile} onChange={(e) => setEditForm({ ...editForm, owner_mobile: e.target.value.replace(/\D/g, "") })} className="am-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Address</label>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Village" value={editForm.village} onChange={(e) => setEditForm({ ...editForm, village: e.target.value })} className="am-input" />
                  <Input placeholder="District" value={editForm.district} onChange={(e) => setEditForm({ ...editForm, district: e.target.value })} className="am-input" />
                  <Input placeholder="State" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} className="am-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Plan</label>
                  <Select value={editForm.plan_id} onValueChange={(v) => setEditForm({ ...editForm, plan_id: v })}>
                    <SelectTrigger className="am-input" data-testid="admin-edit-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">Expiry date</label>
                  <Input data-testid="admin-edit-expiry" type="date" value={editForm.subscription_expires_at || ""} onChange={(e) => setEditForm({ ...editForm, subscription_expires_at: e.target.value })} className="am-input" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditingSub(null)}>{t("common.cancel")}</Button>
            <Button data-testid="admin-edit-save" disabled={editBusy} onClick={submitEdit} className="am-btn-primary">{editBusy ? "…" : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DIALOG (upgrade / downgrade / archive / restore / permanent) */}
      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`font-display flex items-center gap-2 ${confirm?.danger ? "text-destructive" : ""}`}>
              {confirm?.danger && <AlertTriangle className="w-5 h-5" />}
              {confirm?.verb || "Confirm"}
            </DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">{confirm?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
            <Button
              data-testid="admin-confirm-btn"
              disabled={confirmBusy}
              onClick={runConfirm}
              className={confirm?.danger ? "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90" : "am-btn-primary"}
            >
              {confirmBusy ? "…" : confirm?.verb}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MANAGE PASHU HAATS (multi-haat / Enterprise) */}
      <Dialog open={!!haatsFor} onOpenChange={(v) => { if (!v) setHaatsFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="admin-haats-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Store className="w-5 h-5 text-primary" /> {haatsFor?.haat_name}</DialogTitle>
            <DialogDescription>
              Pashu Haats for this subscriber ({haatsData ? `${haatsData.haats.length} / ${haatsData.haat_limit}` : "…"})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(haatsData?.haats || []).map((h) => (
              <div key={h.haat_id} className="flex items-center gap-2 border rounded-xl px-3 py-2 text-sm" data-testid={`admin-haat-row-${h.haat_id}`}>
                <Store className="w-4 h-4 text-muted-foreground" />
                <span className="font-semibold">{h.name}</span>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Input data-testid="admin-new-haat-name" className="am-input flex-1" placeholder="New haat name" value={newHaat} onChange={(e) => setNewHaat(e.target.value)} />
              <Button data-testid="admin-add-haat-btn" aria-label="Add haat" className="am-btn-primary rounded-full" disabled={haatBusy} onClick={addHaatFor}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
